/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * POPUP CẢNH BÁO MỨC 1 — vỏ hiển thị. **Luật sống ở `lib/popup.ts`**, đây chỉ vẽ.
 *
 * ⛔ Chia như vậy có lý do đo được, không phải thẩm mỹ: hai trong sáu quy tắc (cửa sổ gom
 *   5 giây, ngưỡng bão 20/60 s) **không thử được bằng tay** — dev_device nói thẳng "600 tủ
 *   chưa bao giờ cùng lỗi một lúc". Phép thử duy nhất có thật là phép ÉP, và ép được thì
 *   luật phải nằm ngoài React và ngoài đồng hồ hệ thống.
 *
 * ⚠ Tệp này KHÔNG có nhánh quyết định nào của riêng nó. Nếu có ngày cần thêm một luật,
 *   thêm vào `lib/popup.ts` rồi vẽ ở đây — đừng viết một `if` thứ hai ở tầng JSX.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { EVENT_CANH_BAO_MOI, EVENT_CANH_BAO_DONG, type CanhBaoMoi, type CanhBaoDong } from '../lib/api';
import { api } from '../lib/api';
// GD-14 vòng 2 mục 5 — cảnh báo đi qua bảng dịch chung như hai bảng nhật ký: mã lạ ra "Sự kiện <MÃ>",
// không ra mã trần. Tên `phanMuc`/`cauChanPopup` giữ nguyên (verify-protocol ghim lời gọi).
import { cauChanPopup, phanMuc } from '../lib/loi_nguoi';
import {
  cauBao, cauPopup, dongBao, duocPopup, nhan, trangThaiRong, tuBaoDaHet, xacNhan,
  type TrangThaiPopup,
} from '../lib/popup';
import { Button } from './ui';

export default function PopupCanhBao() {
  const [tt, setTt] = useState<TrangThaiPopup>(trangThaiRong);
  /** Chỉ để vẽ lại câu "đang tiếp tục (n phút)" — KHÔNG dùng làm đầu vào của luật nào. */
  const [nhipVe, setNhipVe] = useState(0);
  /*
   * ⚠ Thu gọn ĐẶT LẠI khi có cảnh báo MỚI tới (xem `nhan()` bên dưới). Người trực thu gọn cái
   *   đang có, không phải thu gọn mọi thứ sẽ tới — một cái mới phải bung ra, nếu không thì
   *   "thu gọn" lặng lẽ thành "tắt tiếng vĩnh viễn".
   */
  const [thuGon, datThuGon] = useState(false);
  const dangGhi = useRef(false);

  useEffect(() => {
    const nghe = (e: Event) => {
      const d = (e as CustomEvent<CanhBaoMoi>).detail;
      /*
       * 🔴 TRA BẢNG TRƯỚC KHI ĐƯA VÀO LUẬT. `mucDo` trên dây có thể là `null` = *"không có
       *   mức riêng, đọc BANG_MUC theo mã"* — và `duocPopup(null)` trả `false`. Đưa thẳng
       *   `null` vào thì `METER_TIMEOUT` mức 1 theo bảng sẽ không bao giờ popup, mà lỗi ấy
       *   trông y hệt "chưa có cảnh báo nào" (L62).
       */
      const m = phanMuc(d.code, d.mucDo ?? undefined);
      if (duocPopup(m.mucDo)) datThuGon(false);   // cái MỚI phải bung ra — xem ghi chú ở `thuGon`
      setTt((cu) => nhan(cu, {
        id: d.id ?? '', cabinetId: d.cabinetId, code: d.code ?? '',
        message: d.message ?? m.nhan, mucDo: m.mucDo, luc: d.luc ?? Date.now(),
      }));
    };
    window.addEventListener(EVENT_CANH_BAO_MOI, nghe);
    return () => window.removeEventListener(EVENT_CANH_BAO_MOI, nghe);
  }, []);

  /*
   * 🔴 QUY TẮC 7 — TỦ BÁO ĐÃ HẾT ⇒ rút tủ ấy ra (YC-47). Luật ở `lib/popup.ts`; đây chỉ nối
   *   dây. Không có nhánh quyết định nào ở tầng này, đúng giao ước đầu tệp.
   * ⚠ Rút TỪNG TỦ, không xoá cả nhóm: một popup `SAFETY_CUT` có thể đang chở 40 tủ.
   */
  useEffect(() => {
    const ngheDong = (e: Event) => {
      const d = (e as CustomEvent<CanhBaoDong>).detail;
      const ma = d?.codes ?? [];
      if (!d?.cabinetId || ma.length === 0) return;
      setTt((cu) => ma.reduce((tt2, code) => tuBaoDaHet(tt2, d.cabinetId, code), cu));
    };
    window.addEventListener(EVENT_CANH_BAO_DONG, ngheDong);
    return () => window.removeEventListener(EVENT_CANH_BAO_DONG, ngheDong);
  }, []);

  /* Dải bão có một con số đếm phút — nhịp vẽ lại, không phải nhịp của luật. */
  useEffect(() => {
    if (!tt.bao) return;
    const t = setInterval(() => setNhipVe((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [tt.bao]);

  /*
   * QUY TẮC 6 — cái bấm PHẢI vào nhật ký. Ghi hỏng thì **không đóng popup**: đóng mà không
   * ghi được là mất đúng thứ quy tắc 6 sinh ra để giữ (ai xác nhận, lúc nào).
   */
  const bamXacNhan = async () => {
    if (dangGhi.current) return;
    const id = tt.dangHien?.idMoiNhat;
    dangGhi.current = true;
    try {
      if (id) await api.post(`/api/alarms/${id}/ack`);
      setTt((cu) => xacNhan(cu).tt);
    } catch {
      /* để nguyên popup — người trực bấm lại, hoặc gọi người có quyền */
    } finally {
      dangGhi.current = false;
    }
  };

  if (tt.bao) {
    return (
      <div className="fixed inset-x-0 top-0 z-[60] border-b border-nguy/50 bg-nguy-nen px-4 py-2 text-chu-1 shadow-lg">
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          <ShieldAlert className="h-5 w-5 flex-none" />
          <span className="flex-1 text-sm font-semibold">{cauBao(tt.bao, Date.now() + nhipVe * 0)}</span>
          <Button size="sm" variant="secondary" onClick={() => setTt(dongBao)}>Đã xem</Button>
        </div>
      </div>
    );
  }

  if (!tt.dangHien) return null;

  const p = tt.dangHien;
  const m = phanMuc(p.code);
  const con = tt.hangCho.length;

  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * 🔴 BÁO ĐƯỢC MÀ KHÔNG CHẮN ĐƯỜNG LÀM VIỆC — lee chốt 07/09: *"xét đến cách trình bày, làm
   *   khó chịu người dùng"*.
   *
   * ⛔ BẢN CŨ LÀ MỘT CỬA SỔ CHẶN: `fixed inset-0` + `bg-black/40` phủ kín màn hình. Người trực
   *   đang giữa một thao tác — đang gõ lịch, đang bấm ngõ ra — bị chặn tay cho tới khi bấm
   *   xác nhận. Và một cảnh báo chặn tay là một cảnh báo người ta học cách bấm-cho-xong.
   *   Bấm-cho-xong đúng bằng KHÔNG ĐỌC, mà lại còn ghi vào nhật ký rằng đã đọc.
   *
   * ✅ Nay: thẻ góc, KHÔNG phủ nền, KHÔNG chặn chuột ở chỗ khác. Vẫn đỏ, vẫn đứng trên cùng,
   *   vẫn không tự tắt (quy tắc 6 không đổi — mức 1 chỉ tắt khi có người xác nhận).
   *
   * ⚠ VÀ NÚT "THU GỌN" KHÔNG PHẢI NÚT ĐÓNG. Nó co thẻ thành một chấm đỏ vẫn nằm đó, đếm số
   *   cảnh báo đang chờ. Người trực dời nó đi để làm nốt việc, chứ không làm nó biến mất —
   *   khoảng cách giữa hai chuyện ấy đúng bằng khoảng cách giữa "chưa xử lý" và "đã bỏ qua".
   *   ⛔ Không có đường nào từ đây gọi `xacNhan()` ngoài nút xác nhận. Thu gọn KHÔNG ghi
   *     nhật ký, vì thu gọn không phải một lần đọc.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  if (thuGon) {
    return (
      <button
        onClick={() => datThuGon(false)}
        title="Còn cảnh báo mức 1 chưa xác nhận — bấm để mở lại"
        className="fixed bottom-16 right-4 z-[60] flex items-center gap-2 rounded-full border border-nguy/60
                   bg-nguy-nen px-3 py-2 text-sm font-semibold text-chu-1 shadow-lg hover:bg-nguy/20"
      >
        <AlertTriangle className="h-4 w-4 text-nguy" />
        {con > 0 ? `${con + 1} cảnh báo` : '1 cảnh báo'}
      </button>
    );
  }

  return (
    /*
     * ⛔ `pointer-events-none` ở LỚP NGOÀI, `pointer-events-auto` ở THẺ. Lớp ngoài chỉ để định
     *   vị; nếu nó ăn chuột thì cả một dải màn hình chết dù trông như trống. Đó là cách một
     *   thẻ "không chặn" vẫn chặn.
     */
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-end p-3 sm:p-4">
      <div className="pointer-events-auto w-full max-w-sm rounded-xl border border-nguy/60 bg-nen-1 p-3 shadow-2xl">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-nguy" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-chu-1">{cauPopup(p, m.nhan)}</p>
            {p.moTa && <p className="mt-1 break-words text-xs text-chu-2">{p.moTa}</p>}
            {/*
              🔴 CÂU NÀY TỪNG GÕ CỨNG *"KHÔNG tự tắt"* cho MỌI mã mức 1 — và nó hết đúng từ
              NV92, khi tủ bắt đầu tự khai lúc nhả cổng an toàn (YC-49). Nay hỏi bảng.
              ⛔ Mã tự tắt KHÔNG được nói "cần xử lý ngay": tủ sẽ tự đóng sau 60 s điện ổn, và
                 một cảnh báo kêu khi không có việc để làm sẽ bị phớt lờ — rồi lần thật cũng vậy.
            */}
            <p className="mt-2 text-xs text-chu-3">{cauChanPopup(p.code)}</p>
          </div>
          {/* QUY TẮC 3 — cái sau đứng thành huy hiệu trên chính thẻ này, không chồng cửa sổ. */}
          {con > 0 && (
            <span className="flex-none rounded-full bg-nguy px-2 py-0.5 text-xs font-semibold text-chu-1">
              +{con}
            </span>
          )}
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => datThuGon(true)}>Thu gọn</Button>
          <Button size="sm" variant="primary" onClick={() => void bamXacNhan()}>Đã xem, xác nhận</Button>
        </div>
      </div>
    </div>
  );
}
